import React from "react";
import { Activity, Target, AlertTriangle, TrendingUp, HelpCircle } from "lucide-react";

const SwotDocs = () => {
  return (
    <div className="space-y-12 pb-20">
      {/* Header */}
      <section>
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 border border-blue-100 mb-4">
          <Activity size={14} className="text-blue-600" />
          <span className="text-[10px] font-black text-blue-600 uppercase tracking-widest">Coaching Tool</span>
        </div>
        <h1 className="text-4xl font-black text-gray-900 tracking-tight mb-4">The Development Blueprint</h1>
        <p className="text-lg text-gray-600 max-w-2xl leading-relaxed">
          The SWOT page allows you to translate athletic data into a coaching strategy. 
          Use this to define the "How" and "Why" behind an athlete's training path.
        </p>
      </section>

      {/* Feature Breakdown */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="p-6 bg-white border border-gray-100 rounded-2xl shadow-sm">
            <div className="flex items-center gap-2 mb-4">
                <Target className="text-green-600" size={20} />
                <h4 className="font-bold">Prioritized Growth</h4>
            </div>
            <p className="text-xs text-gray-500 leading-relaxed">
                Use the <strong>Drag-and-Drop</strong> handle to reorder items. This signals 
                to the athlete which specific weakness or opportunity is their #1 priority 
                for the upcoming week.
            </p>
        </div>

        <div className="p-6 bg-white border border-gray-100 rounded-2xl shadow-sm">
            <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="text-blue-600" size={20} />
                <h4 className="font-bold">Actionable Outcomes</h4>
            </div>
            <p className="text-xs text-gray-500 leading-relaxed">
                A SWOT item without an <strong>Action Plan</strong> is just a complaint. 
                Always provide 1–3 specific steps (e.g., "15 minutes of wall-ball per day") 
                to bridge the gap.
            </p>
        </div>
      </div>

      {/* The Quadrant Map */}
      <section className="bg-slate-900 rounded-[2.5rem] p-10 text-white">
        <h3 className="text-2xl font-bold mb-8 flex items-center gap-3">
            <HelpCircle className="text-blue-400" /> Defining the Quadrants
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="p-6 bg-slate-800 rounded-2xl border-l-4 border-green-500">
                <span className="text-[10px] font-black text-green-500 uppercase italic">Strengths</span>
                <p className="text-sm mt-2 text-slate-300">Skills to leverage for playing time and recruiting highlights.</p>
            </div>
            <div className="p-6 bg-slate-800 rounded-2xl border-l-4 border-red-500">
                <span className="text-[10px] font-black text-red-500 uppercase italic">Weaknesses</span>
                <p className="text-sm mt-2 text-slate-300">Immediate inhibitors that require technical correction.</p>
            </div>
            <div className="p-6 bg-slate-800 rounded-2xl border-l-4 border-blue-500">
                <span className="text-[10px] font-black text-blue-500 uppercase italic">Opportunities</span>
                <p className="text-sm mt-2 text-slate-300">Long-term growth areas that could change an athlete's ceiling.</p>
            </div>
            <div className="p-6 bg-slate-800 rounded-2xl border-l-4 border-yellow-500">
                <span className="text-[10px] font-black text-yellow-500 uppercase italic">Threats</span>
                <p className="text-sm mt-2 text-slate-300">Lifestyle or physical risks that could derail a season.</p>
            </div>
        </div>
      </section>

      {/* Coach Advice */}
      <div className="pt-8 text-center italic text-gray-400 text-xs">
        "A well-maintained SWOT profile is the most valuable asset in a recruiting conversation. 
        It shows scouts that the athlete is coachable and self-aware."
      </div>
    </div>
  );
};

export default SwotDocs;